import { useMemo } from "react";
import { AnimatedCounter } from "./AnimatedCounter";
import { useAnimateOnScroll } from "@/hooks/useAnimateOnScroll";
import { useScrollProgress } from "@/hooks/useScrollProgress";
import { CalendarDays, Home, TrendingUp, Clock, Zap } from "lucide-react";

interface ShowingEvent {
  scheduled_date: string;
  event_type: string;
}

interface SpotlightShowingsProps {
  showings: ShowingEvent[];
  primaryColor?: string | null;
}

function getMonthName(month: number): string {
  return ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][month];
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

interface CalendarDay {
  date: Date;
  showings: number;
  openHouses: number;
}

export function SpotlightShowings({ showings, primaryColor }: SpotlightShowingsProps) {
  const { ref: animRef, isVisible } = useAnimateOnScroll(0.15);
  const { ref: scrollRef, progress } = useScrollProgress();

  if (showings.length === 0) return null;

  const {
    calendarWeeks,
    monthLabel,
    totalShowings,
    totalOpenHouses,
    busiestDay,
    avgPerWeek,
    recentTrend,
  } = useMemo(() => {
    // Build dateMap for ALL events (used to render calendar dots across the grid)
    const dateMap = new Map<string, { showings: number; openHouses: number }>();
    for (const s of showings) {
      const dateKey = s.scheduled_date.split("T")[0];
      const existing = dateMap.get(dateKey) || { showings: 0, openHouses: 0 };
      if (s.event_type === "open_house") existing.openHouses++;
      else existing.showings++;
      dateMap.set(dateKey, existing);
    }

    // Always anchor to today (matching Activity page heatmap) so both views show the same window
    const today = new Date();
    const endOfWeek = new Date(today);
    endOfWeek.setDate(endOfWeek.getDate() + (6 - endOfWeek.getDay()));
    const startDate = new Date(endOfWeek);
    startDate.setDate(startDate.getDate() - 27);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    const winStartKey = startDate.toISOString().split("T")[0];
    const winEndKey = endOfWeek.toISOString().split("T")[0];

    const weeks: CalendarDay[][] = [];
    let currentDate = new Date(startDate);
    while (currentDate <= endOfWeek) {
      const week: CalendarDay[] = [];
      for (let d = 0; d < 7; d++) {
        const dateKey = currentDate.toISOString().split("T")[0];
        const data = dateMap.get(dateKey);
        week.push({
          date: new Date(currentDate),
          showings: data?.showings ?? 0,
          openHouses: data?.openHouses ?? 0,
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }
      weeks.push(week);
    }

    const label = `${getMonthName(startDate.getMonth())} ${startDate.getFullYear()}${
      endOfWeek.getMonth() !== startDate.getMonth()
        ? ` - ${getMonthName(endOfWeek.getMonth())} ${endOfWeek.getFullYear()}`
        : ""
    }`;

    // Stats: count only events within the displayed window (matches Activity page logic)
    let totalS = 0;
    let totalOH = 0;
    const dayOfWeekCounts = [0, 0, 0, 0, 0, 0, 0];
    for (const [key, v] of dateMap) {
      if (key >= winStartKey && key <= winEndKey) {
        totalS += v.showings;
        totalOH += v.openHouses;
        const dow = new Date(key + "T12:00:00").getDay();
        dayOfWeekCounts[dow] += v.showings + v.openHouses;
      }
    }

    const busiestDayIdx = dayOfWeekCounts.indexOf(Math.max(...dayOfWeekCounts));
    const totalEvents = totalS + totalOH;
    const numWeeks = weeks.length || 1;
    const avg = Math.round((totalEvents / numWeeks) * 10) / 10;

    // Recent trend: compare last half of weeks to first half
    const midpoint = Math.floor(weeks.length / 2);
    const firstHalf = weeks.slice(0, midpoint).flat().reduce((s, d) => s + d.showings + d.openHouses, 0);
    const secondHalf = weeks.slice(midpoint).flat().reduce((s, d) => s + d.showings + d.openHouses, 0);
    const trend = firstHalf === 0 ? "increasing" : secondHalf > firstHalf ? "increasing" : secondHalf < firstHalf ? "cooling" : "steady";

    return {
      calendarWeeks: weeks,
      monthLabel: label,
      totalShowings: totalS,
      totalOpenHouses: totalOH,
      busiestDay: DAY_NAMES[busiestDayIdx],
      avgPerWeek: avg,
      recentTrend: trend,
    };
  }, [showings]);

  const markerColor = primaryColor ?? "#3B82F6";
  const parallaxY = (progress - 0.5) * -20;

  return (
    <section ref={scrollRef} className="w-full py-16 md:py-24 px-6" style={{ background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 50%, #f8fafc 100%)" }}>
      <div
        ref={animRef}
        className="max-w-6xl mx-auto will-change-transform"
        style={{ transform: `translateY(${parallaxY}px)` }}
      >
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400 mb-12">
          Showing Activity
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left column — Calendar heat map */}
          <div
            className={`transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <p className="text-xs text-slate-400 mb-4">{monthLabel}</p>
            <div className="flex gap-3 text-xs text-slate-400 mb-2">
              {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                <div key={i} className="w-12 h-5 flex items-center justify-center">{d}</div>
              ))}
            </div>
            <div className="space-y-3">
              {calendarWeeks.map((week, wi) => (
                <div
                  key={wi}
                  className={`flex gap-3 transition-all duration-500 ${
                    isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
                  }`}
                  style={{ transitionDelay: `${400 + wi * 60}ms` }}
                >
                  {week.map((day, di) => {
                    const count = day.showings + day.openHouses;
                    const opacity = count === 0 ? 0.06 : Math.min(0.2 + count * 0.2, 1);
                    return (
                      <div
                        key={di}
                        className="w-12 h-12 rounded-full shadow-sm hover:scale-110 transition-transform cursor-default"
                        style={{ backgroundColor: markerColor, opacity }}
                        title={
                          count > 0
                            ? `${day.date.toLocaleDateString()}: ${count} event${count > 1 ? "s" : ""}`
                            : day.date.toLocaleDateString()
                        }
                      />
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-5 text-xs text-slate-400">
              <span>Less</span>
              {[0.06, 0.2, 0.4, 0.6, 1].map((o, i) => (
                <div
                  key={i}
                  className="w-5 h-5 rounded-full"
                  style={{ backgroundColor: markerColor, opacity: o }}
                />
              ))}
              <span>More</span>
            </div>
          </div>

          {/* Right column — Stats + AI insights */}
          <div className="flex flex-col gap-8">
            {/* Stats row */}
            <div
              className={`grid grid-cols-2 gap-6 transition-all duration-700 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
              }`}
            >
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-3" style={{ backgroundColor: `${markerColor}15` }}>
                  <CalendarDays className="h-5 w-5" style={{ color: markerColor }} />
                </div>
                <AnimatedCounter
                  value={totalShowings.toString()}
                  className="text-3xl md:text-4xl font-bold text-slate-900 block"
                  duration={1800}
                />
                <p className="text-xs text-slate-400 uppercase tracking-wider mt-1">Showings</p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-3" style={{ backgroundColor: `${markerColor}15` }}>
                  <Home className="h-5 w-5" style={{ color: markerColor }} />
                </div>
                <AnimatedCounter
                  value={totalOpenHouses.toString()}
                  className="text-3xl md:text-4xl font-bold text-slate-900 block"
                  duration={1800}
                />
                <p className="text-xs text-slate-400 uppercase tracking-wider mt-1">Open Houses</p>
              </div>
            </div>

            {/* AI Insights */}
            <div
              className={`space-y-3 transition-all duration-700 delay-300 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-4">Activity Insights</p>

              <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${markerColor}10` }}>
                  <Zap className="h-4 w-4" style={{ color: markerColor }} />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">Busiest day is {busiestDay}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Most showing activity happens on {busiestDay}s</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${markerColor}10` }}>
                  <Clock className="h-4 w-4" style={{ color: markerColor }} />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">{avgPerWeek} events per week</p>
                  <p className="text-xs text-slate-400 mt-0.5">Average weekly showing and open house activity</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${markerColor}10` }}>
                  <TrendingUp className="h-4 w-4" style={{ color: markerColor }} />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    Recent activity is {recentTrend}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {recentTrend === "increasing"
                      ? "Buyer interest is picking up with more showings recently"
                      : recentTrend === "cooling"
                        ? "Showing pace has eased compared to earlier weeks"
                        : "Showing activity has been consistent throughout the period"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
